import { Component, HostListener } from '@angular/core';

import {
    ThemeLoaderService,
    Debounce,
    DocumentRef,
    STORAGEKEY_THEME,
} from '@kpmg-tax/ui';
import { STORAGEKEY_ACTIVITY_MONITOR_CONFIG_OVERRIDE, STORAGEKEY_LAST_CANCELLED_LOGOUT, STORAGEKEY_LAST_CONFIRMED_LOGOUT, STORAGEKEY_LAST_PENDING_LOGOUT, STORAGEKEY_LAST_SESSION_ACTIVITY, STORAGEKEY_LAST_SESSION_EXTENSION, STORAGEKEY_LAST_SESSION_HEARTBEAT, STORAGEKEY_LAST_SESSION_WARNING } from '../../core/services/app-context/storage-keys.constants';
import {
    ActivityMonitorService,
    ActivityMonitorConfig,
    ConfigChangeRequest,
} from '../activity-monitor';
import { SessionMgmtService } from '../session-mgmt';

@Component({
    selector: 'storage-monitor',
    template: '',
})
export class StorageMonitorComponent {
    private _isVisible: boolean;
    private _hiddenPropName: string;

    constructor(
        private _activityMonitorService: ActivityMonitorService,
        private _sessionMgmtService: SessionMgmtService,
        private _themeLoaderService: ThemeLoaderService,
        private _documentRef: DocumentRef,
    ) {
        this._isVisible = true;

        // Based on the browser, setup the proper "hidden" property name for the tab/window
        if (typeof _documentRef.nativeDocument.mozHidden !== 'undefined') {
            this._hiddenPropName = 'mozHidden';
        } else if (typeof _documentRef.nativeDocument.msHidden !== 'undefined') {
            this._hiddenPropName = 'msHidden';
        } else if (
            typeof _documentRef.nativeDocument.webkitHidden !== 'undefined'
        ) {
            this._hiddenPropName = 'webkitHidden';
        } else {
            this._hiddenPropName = 'hidden';
        }
    }

    // This event only fires when storage has changed in other windows/tabs
    @HostListener('window:storage', ['$event'])
    @Debounce()
    onStorageUpdated($event: any): void {
        switch ($event.key) {
            case STORAGEKEY_THEME:
                const theme = $event.newValue;
                // Do not store the new value since that has already been done and would trigger an infinite event loop
                this._themeLoaderService.setActiveTheme(theme, false);
                break;

            case STORAGEKEY_LAST_PENDING_LOGOUT:
                this._sessionMgmtService.logoutPending(false);
                break;

            case STORAGEKEY_LAST_CONFIRMED_LOGOUT:
                this._sessionMgmtService.logoutConfirmed(false);
                break;

            case STORAGEKEY_LAST_CANCELLED_LOGOUT:
                this._sessionMgmtService.logoutCancelled(false);
                break;

            case STORAGEKEY_LAST_SESSION_ACTIVITY:
                this._activityMonitorService.activityDetected(false);
                break;

            case STORAGEKEY_LAST_SESSION_HEARTBEAT:
                this._activityMonitorService.heartbeat(false);
                break;

            case STORAGEKEY_LAST_SESSION_WARNING:
                this._activityMonitorService.sessionWarning(false);
                break;

            case STORAGEKEY_LAST_SESSION_EXTENSION:
                this._activityMonitorService.sessionExtended(false);
                break;

            case STORAGEKEY_ACTIVITY_MONITOR_CONFIG_OVERRIDE:
                const config: ActivityMonitorConfig = JSON.parse($event.newValue);

                const changeRequest: ConfigChangeRequest = {
                    isEventSource: false,
                    config: config,
                };

                this._activityMonitorService.changeConfig(changeRequest);
                break;

            default:
                break;
        }
    }

    // This event only fires when windows/tab changes visibility. We add a event listener for all brower-specific event names.
    @HostListener('window:mozvisibilitychange', ['$event'])
    @HostListener('window:msvisibilitychange', ['$event'])
    @HostListener('window:webkitvisibilitychange', ['$event'])
    @HostListener('window:visibilitychange', ['$event'])
    @Debounce()
    onVisibilityChanged($event: any): void {
        this._isVisible = this._documentRef.nativeDocument[this._hiddenPropName];
    }
}
